import React, { createContext, Fragment, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation, useHistory } from "react-router-dom";
import {
  concat,
  equals,
  isEmpty,
  join,
  last,
  length,
  split,
  uniq,
} from "ramda";
import { allTrue, isBlank, notBlank } from "../../util/ext";
import {
  buildExpandedId,
  buildBreadcrumbs,
  getReviewHome,
  isAnyReview,
  isContentGroup,
  isContentGroupView,
  isProcessing,
  OrganizeViews,
  useSearchParam,
} from "./organizeUtil";
import { useSidebar } from "./OrganizeProvider";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DragItemTypes from "./DragItemTypes";
import {
  hasAnyRole,
  organizeRoles,
  useIsAdmin,
  useIsIapReviewer,
} from "../../auth/authUtils";
import { useSelector } from "react-redux";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";

import IncludeDialog from "./include-in/IncludeDialog";
import NotFound from "./common/NotFound";
import OrganizeAnthologyBrowser from "./content-groups/OrganizeAnthologyBrowser";
import OrganizeCollectionBrowser from "./content-groups/OrganizeCollectionBrowser";
import OrganizeProcessingBrowser from "./review/OrganizeProcessingBrowser";
import OrganizeReviewBrowser from "./review/OrganizeReviewBrowser";
import ContentGroupsBrowser from "./content-groups/ContentGroupsBrowser";
import CustomDragLayer from "./CustomDragLayer";
import OrganizeModuleBrowser from "./content-groups/OrganizeModuleBrowser";
import OrganizeSidebar from "./sidebar/OrganizeSidebar";
import Unauthorized from "../../auth/Unauthorized";
import UngroupedItemsBrowser from "./content-groups/UngroupedItemsBrowser";
import UploadMediaButton from "./upload/UploadMediaButton";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";

export const BROWSER_OFFSET = "200px";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    borderRadius: 20,
    padding: 0,
  },

  browser: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(6),
    paddingBottom: 10,
    borderRadius: 20,
    backgroundColor: "white",
    height: "auto",
  },

  grid: {
    height: `calc(100vh - ${BROWSER_OFFSET})`,
  },

  sidebar: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(0, 3, 0, 3),
    alignContent: "flex-start",
  },
}));

const UnauthorizedAccess = () => (
  <Unauthorized message="You do not have the proper access rights to the Creator Application" />
);

const isAuthorized = auth =>
  notBlank(auth) && hasAnyRole(auth.currentOrganization, organizeRoles);

const notValidId = id => length(id) !== 36;

const initialGroupBrowserState = {
  expanded: {
    modules: {
      outer: true,
      inner: false,
    },
    contentGroups: {
      outer: true,
      inner: false,
    },
  },

  loading: false,
  setLoading: () => {},
};
export const GroupBrowserContext = createContext(initialGroupBrowserState);
const GroupBrowser = props => {
  const { view, id } = props;
  const [expanded, setExpanded] = useState(initialGroupBrowserState.expanded);
  const [loading, setLoading] = useState(initialGroupBrowserState.loading);

  useEffect(() => {
    if (view) {
      setExpanded(initialGroupBrowserState.expanded);
    }
  }, [view]);

  const Browser = equals(view, OrganizeViews.MODULE)
    ? OrganizeModuleBrowser
    : equals(view, OrganizeViews.ANTHOLOGY)
    ? OrganizeAnthologyBrowser
    : equals(view, OrganizeViews.COLLECTION)
    ? OrganizeCollectionBrowser
    : equals(view, OrganizeViews.UNGROUPED_ITEMS)
    ? UngroupedItemsBrowser
    : ContentGroupsBrowser;

  if (notBlank(id) && notValidId(id)) {
    return <NotFound />;
  }

  if (isContentGroup(view) && isBlank(id)) {
    return <NotFound />;
  }

  return (
    <GroupBrowserContext.Provider
      value={{
        expanded,
        setExpanded,
        loading,
        setLoading,
      }}
    >
      <Browser {...props} />
    </GroupBrowserContext.Provider>
  );
};

const OrganizeView = ({ SnackAlertHandler }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isAdmin = useIsAdmin();
  const isIapReviewer = useIsIapReviewer();
  const auth = useSelector(state => state.auth);
  const searchParam = useSearchParam();
  const { updateExpanded } = useSidebar();

  //-- Manage Sidebar State
  const [view, setView] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState("");
  const [expanded, setExpanded] = useState([
    OrganizeViews.REVIEW,
    OrganizeViews.ORGANIZE,
  ]);

  const handleExpanded = (event, nodeIds) => {
    const ids = uniq(nodeIds);
    updateExpanded(ids);
    setExpanded(ids);
  };

  const viewingContent = ids => notBlank(ids);

  //-- Manage Sidebar State --//
  //-- [Tyler]: We can programtically expand + highlight MUI TreeItems
  //-- by keeping track of which TreeItem node ids are active.
  //-- This is driven by the url search params: view and id.
  //-- view lists the tree order, eg. organize,organize-grouped,module,anthology
  //-- id lists the media ids from parent to child, eg. module.id,anthology.id
  //-- Because children (anthologies, collections) can be in multiple parents,
  //-- node ids need to be more unique than mediaIds.
  //-- The node id of the child of a grouped media item follows this pattern:
  //-- organize-organize-grouped-${parentMediaType}-${parentId}-${childMediaType}-${childId}
  useEffect(() => {
    const view = searchParam.get("view");
    const id = searchParam.get("id");

    const views = isBlank(view)
      ? [OrganizeViews.REVIEW, OrganizeViews.PENDING]
      : split(",", view);
    setView(last(views));

    const ids = notBlank(id) ? split(",", id) : [];

    const breadcrumbs = buildBreadcrumbs({ views, ids });
    setBreadcrumbs(breadcrumbs);

    // highlight + expand tree items
    if (viewingContent(ids)) {
      // id for content group browser
      setId(last(ids));

      const treeItems = concat(views, ids);
      setSelected(join("-", treeItems));
    } else {
      setSelected(last(views));
    }

    const expanded = buildExpandedId({ views, ids });
    setExpanded(prev => {
      const updated = uniq(concat(prev, expanded));
      updateExpanded(updated);
      return updated;
    });
  }, [searchParam, updateExpanded]);

  //-- Manage Search Param
  useEffect(() => {
    const search = location.search
      ? location.search
      : `?view=${getReviewHome()}`;

    if (isBlank(location.state)) {
      history.replace(location.pathname + search);
    }
  }, [history, location.state, location.pathname, location.search]);

  //-- When dropping an item in a non-drop zone,
  //-- there is a noticeable delay onDragEnd.
  //-- This is a known issue that has not been fixed,
  //-- but the code below serves as a workaround
  //-- by making the entire body a drop zone.
  const [_, bodyDropRef] = useDrop(() => ({
    accept: [
      DragItemTypes.CONTENT,
      DragItemTypes.COLLECTION,
      DragItemTypes.ANTHOLOGY,
    ],
  }));

  useEffect(() => {
    bodyDropRef(document.body);
    return () => {
      bodyDropRef(null);
    };
  });

  return (
    <Fragment>
      {isAdmin || isAuthorized(auth) ? (
        <DndProvider backend={HTML5Backend}>
          <CustomDragLayer />

          <IncludeDialog SnackAlertHandler={SnackAlertHandler} />

          <Container className={classes.root} id="application-panel-organize">
            <Grid className={classes.grid} container>
              <Grid
                className={classes.sidebar}
                item
                xs={3}
                md={2}
                lg={3}
                xl={2}
              >
                {allTrue([!isAdmin, !isIapReviewer]) && <UploadMediaButton />}

                <OrganizeSidebar
                  handleExpanded={handleExpanded}
                  selected={selected}
                  expanded={expanded}
                />
              </Grid>
              <Grid
                className={classes.browser}
                item
                xs={9}
                md={10}
                lg={9}
                xl={10}
              >
                {allTrue([
                  isIapReviewer,
                  !isAnyReview(view),
                  !isEmpty(view),
                ]) ? (
                  <UnauthorizedAccess />
                ) : isContentGroupView(view) ? (
                  <GroupBrowser
                    view={view}
                    id={id}
                    breadcrumbs={breadcrumbs}
                    SnackAlertHandler={SnackAlertHandler}
                  />
                ) : isAnyReview(view) ? (
                  <OrganizeReviewBrowser
                    SnackAlertHandler={SnackAlertHandler}
                  />
                ) : isProcessing(view) ? (
                  <OrganizeProcessingBrowser />
                ) : isEmpty(view) ? (
                  <PanelLoadingIndicator />
                ) : (
                  <NotFound />
                )}
              </Grid>
            </Grid>
          </Container>
        </DndProvider>
      ) : (
        <UnauthorizedAccess />
      )}
    </Fragment>
  );
};

export default OrganizeView;
